import * as z from 'zod';
import { TFunction } from 'i18next';
import dayjs from 'dayjs';
import { requiredString } from '../utils/validation';
import { createPhoneSchema } from './helpers/phoneValidation';

export const createRegisterFieldsSchemas = (t: TFunction) => ({
  email: (required: boolean) => {
    const baseValidation = z
      .string({ error: t('register.validation.requiredField') })
      .pipe(z.email(t('register.validation.invalidEmail')));

    return required ? baseValidation : baseValidation.optional().or(z.literal(''));
  },

  password: (required: boolean) => {
    const baseValidation = z
      .string({ error: t('register.validation.requiredField') })
      .min(8, t('register.validation.passwordTooShort'));

    return required ? baseValidation : baseValidation.optional().or(z.literal(''));
  },

  firstName: (required: boolean) => {
    return required ? requiredString(t) : z.string().optional();
  },

  lastName: (required: boolean) => {
    return required ? requiredString(t) : z.string().optional();
  },

  phone: (required: boolean) => createPhoneSchema(t, required),

  address: (required: boolean) => {
    return required ? requiredString(t) : z.string().optional();
  },

  city: (required: boolean) => {
    const baseValidation = z.string().min(2, t('register.validation.minLength', { min: 2 }));
    return required ? baseValidation : baseValidation.optional().or(z.literal(''));
  },

  region: (required: boolean) => {
    const baseValidation = z.string().min(2, t('register.validation.minLength', { min: 2 }));
    return required ? baseValidation : baseValidation.optional().or(z.literal(''));
  },

  country: (required: boolean) => {
    return required ? requiredString(t) : z.string().optional();
  },

  zip: (required: boolean) => {
    const baseValidation = z.string().min(4, t('register.validation.minLength', { min: 4 }));
    return required ? baseValidation : baseValidation.optional().or(z.literal(''));
  },

  currency: (required: boolean) => {
    return required ? z.string().min(1, t('register.validation.currencyRequired')) : z.string().optional();
  },

  ageVerified: (required: boolean) => {
    return required
      ? z.boolean().refine((val) => val === true, {
          error: t('register.validation.ageConfirmation'),
        })
      : z.boolean().optional();
  },

  dateOfBirth: (required: boolean) => {
    const dateValidation = (val: string) => {
      const date = dayjs(val, 'YYYY-MM-DD', true);
      return date.isValid() && dayjs().diff(date, 'year') >= 18;
    };
    const errorMessage = t('register.validation.minAge18');

    if (required) {
      return requiredString(t).refine(dateValidation, { error: errorMessage });
    } else {
      return z
        .string()
        .optional()
        .refine((val) => !val || val === '' || dateValidation(val), {
          error: errorMessage,
        });
    }
  },

  code: (required: boolean) => {
    return required ? requiredString(t) : z.string().optional();
  },
});

export type FieldSchemaKey = keyof ReturnType<typeof createRegisterFieldsSchemas>;
